import * as d3 from 'd3';
import { DateTime } from 'luxon';
import {
    getMeasurementDelayBucket,
    getMeasurementDelayBucketColor,
    NUM_MEASUREMENT_DELAY_BUCKETS,
} from '../../../../shared/services/measurementDelayBuckets.ts';
import {
    MeasurementShiftDelay,
    MeasurementShiftDelayItem,
} from '../../../../shared/types/measurementShiftDelay.ts';
import { createGraphG, drawNoDataMessage, getGraphSvg, GraphG } from '../../../utils/d3Utils.ts';

const SHIFT_ORDER = ['night', 'morning', 'evening'] as const;
const hoverPanelLabelClearance = 56;

interface DelayBarSection {
    minutes: number;
    color: string;
}

export function drawMeasurementShiftDelays(
    container: HTMLDivElement,
    data: MeasurementShiftDelay[],
    dayKeys: string[],
    visibleShifts: MeasurementShiftDelay['shiftSlot'][],
    onShiftClick: (shiftEnd: Date) => void,
) {
    const root = getGraphSvg(container);
    if (dayKeys.length === 0) {
        drawNoDataMessage(root);
        return;
    }

    const margins = { top: 100, right: 50, bottom: 100, left: 105 };
    const graph = createGraphG(root, margins);
    const { width, height } = graph.datum();
    const dayGroups = groupByDay(data, dayKeys);
    const visibleShiftOrder = SHIFT_ORDER.filter((shiftSlot) =>
        visibleShifts.includes(shiftSlot)
    );
    const xDay = d3.scaleBand<string>()
        .domain(dayKeys)
        .range([0, width])
        .padding(0.12);
    const xShift = d3.scaleBand<string>()
        .domain(visibleShiftOrder)
        .range([0, xDay.bandwidth()])
        .padding(0.18);
    const maxDelay = d3.max(data, (shift) => shift.totalDelayMinutes) ?? 0;
    const yDelay = d3.scaleLinear()
        .domain([0, Math.max(1, maxDelay)])
        .nice()
        .range([height, 0]);
    const fontSize = width > 1000 ? 18 : 14;
    const hoverPanel = graph.append('g')
        .style('display', 'none')
        .style('pointer-events', 'none');

    const xAxis = root.append('g')
        .attr('transform', `translate(${margins.left}, ${height + margins.top})`)
        .call(d3.axisBottom(xDay).tickFormat(() => ''));
    xAxis.selectAll<SVGGElement, string>('.tick')
        .each(function (dayKey) {
            const tick = d3.select(this);
            const [dateLabel, weekdayLabel] = formatDayLabel(dayKey);
            tick.select('text').remove();
            visibleShiftOrder.forEach((shiftSlot) => {
                const shiftCenter = xShift(shiftSlot)! + xShift.bandwidth() / 2;
                tick.append('text')
                    .attr('x', shiftCenter - xDay.bandwidth() / 2)
                    .attr('y', fontSize * 1.35)
                    .attr('text-anchor', 'middle')
                    .attr('font-size', `${fontSize}px`)
                    .attr('fill', 'currentColor')
                    .text(getShiftSlotLabel(shiftSlot));
            });
            tick.append('text')
                .attr('x', 0)
                .attr('y', fontSize * 2.9)
                .attr('text-anchor', 'middle')
                .attr('font-size', `${fontSize}px`)
                .attr('fill', 'currentColor')
                .text(dateLabel);
            tick.append('text')
                .attr('x', 0)
                .attr('y', fontSize * 4.15)
                .attr('text-anchor', 'middle')
                .attr('font-size', `${fontSize}px`)
                .attr('fill', 'currentColor')
                .text(weekdayLabel);
        });

    root.append('g')
        .attr('transform', `translate(${margins.left}, ${margins.top})`)
        .call(d3.axisLeft(yDelay))
        .selectAll('text')
        .style('font-size', `${fontSize}px`);

    root.append('text')
        .attr('x', 22)
        .attr('y', margins.top + height / 2)
        .attr('text-anchor', 'middle')
        .attr('transform', `rotate(-90, 22, ${margins.top + height / 2})`)
        .attr('font-size', `${fontSize}px`)
        .text('Total delay (minutes)');

    dayGroups.forEach((dayGroup) => {
        visibleShiftOrder.forEach((shiftSlot) => {
            const barX = xDay(dayGroup.dayKey)! + xShift(shiftSlot)!;
            const shift = dayGroup.shifts.get(shiftSlot);
            if (!shift) return;
            const teamLabel = getTeamLabel(shift);
            const totalDelayLabel = formatBarHours(shift.totalDelayMinutes);
            const countLabel = `(${shift.items.length})`;
            drawBar(
                graph,
                barX,
                xShift.bandwidth(),
                yDelay(shift.totalDelayMinutes),
                height - yDelay(shift.totalDelayMinutes),
                getDelayBarSections(shift.items),
                [
                    teamLabel,
                    totalDelayLabel,
                    countLabel,
                ].filter(Boolean),
                fontSize,
                () => onShiftClick(shift.shiftEnd),
                hoverPanel,
                width,
                [
                    formatDayLabel(dayGroup.dayKey).join(' '),
                    `Shift: ${getShiftSlotLabel(shiftSlot)}${teamLabel ? ` ${teamLabel}` : ''}`,
                    `Total delay: ${totalDelayLabel}`,
                    `Unresolved: ${shift.items.length} delay${shift.items.length === 1 ? '' : 's'}`,
                ],
            );
        });
    });
}

function drawBar(
    graph: ReturnType<typeof createGraphG>,
    x: number,
    width: number,
    y: number,
    height: number,
    sections: DelayBarSection[],
    labels: string[],
    fontSize: number,
    onClick: () => void,
    hoverPanel: GraphG,
    graphWidth: number,
    hoverLines: string[],
) {
    const barElement = graph.append('g')
        .attr('cursor', 'pointer')
        .on('click', onClick)
        .on('mouseenter', function () {
            d3.select(this)
                .select('.measurement-shift-delay-bar-outline')
                .attr('stroke', '#222')
                .attr('stroke-width', 3);
            showHoverPanel(hoverPanel, graphWidth, x + width / 2, y, hoverLines);
        })
        .on('mouseleave', function () {
            d3.select(this)
                .select('.measurement-shift-delay-bar-outline')
                .attr('stroke', null)
                .attr('stroke-width', null);
            hoverPanel.style('display', 'none');
        });

    let sectionBottom = y + height;
    const totalMinutes = d3.sum(sections, (section) => section.minutes);
    sections.forEach((section) => {
        const sectionHeight = totalMinutes === 0 ? 0 : height * section.minutes / totalMinutes;
        sectionBottom -= sectionHeight;
        barElement.append('rect')
            .attr('x', x)
            .attr('y', sectionBottom)
            .attr('width', width)
            .attr('height', sectionHeight)
            .attr('fill', section.color);
    });

    barElement.append('rect')
        .attr('class', 'measurement-shift-delay-bar-outline')
        .attr('x', x)
        .attr('y', y)
        .attr('width', width)
        .attr('height', height)
        .attr('fill', 'none')
        .attr('pointer-events', 'none');

    const labelFontSize = Math.min(fontSize, 16);
    const lineHeight = labelFontSize * 1.15;
    labels.forEach((label, labelIndex) => {
        barElement.append('text')
            .attr('x', x + width / 2)
            .attr('y', y - 6 - (labels.length - 1 - labelIndex) * lineHeight)
            .attr('text-anchor', 'middle')
            .attr('font-size', `${labelFontSize}px`)
            .attr('cursor', 'pointer')
            .text(label);
    });
}

function getDelayBarSections(items: MeasurementShiftDelayItem[]): DelayBarSection[] {
    const minutesByBucket = new Array<number>(NUM_MEASUREMENT_DELAY_BUCKETS).fill(0);
    items.forEach((item) => {
        const bucket = getMeasurementDelayBucket(item.delayMinutes * 60_000);
        minutesByBucket[bucket] += item.delayMinutes;
    });

    return minutesByBucket
        .map((minutes, bucket) => ({
            minutes,
            color: getMeasurementDelayBucketColor(bucket),
        }))
        .filter((section) => section.minutes > 0);
}

function showHoverPanel(
    panel: GraphG,
    graphWidth: number,
    barX: number,
    barY: number,
    lines: string[],
) {
    const lineHeight = 22;
    const panelPaddingX = 12;
    const widestLine = lines.reduce((widest, line) => Math.max(widest, line.length), 0);
    const panelWidth = Math.max(150, Math.min(290, widestLine * 8 + panelPaddingX * 2));
    const panelHeight = 18 + lines.length * lineHeight;
    const panelX = Math.max(8, Math.min(graphWidth - panelWidth - 8, barX - panelWidth / 2));
    const panelY = Math.max(8, barY - panelHeight - hoverPanelLabelClearance);

    panel
        .style('display', null)
        .attr('transform', `translate(${panelX}, ${panelY})`);
    panel.selectAll('*').remove();
    panel.append('rect')
        .attr('width', panelWidth)
        .attr('height', panelHeight)
        .attr('rx', 6)
        .attr('fill', '#fff')
        .attr('fill-opacity', 0.96)
        .attr('stroke', '#555');
    lines.forEach((line, index) => {
        panel.append('text')
            .attr('x', panelPaddingX)
            .attr('y', 23 + index * lineHeight)
            .attr('font-family', 'Arial')
            .attr('font-size', index === 0 ? 16 : 14)
            .attr('font-weight', index === 0 ? 700 : 400)
            .attr('fill', '#111')
            .text(line);
    });
    panel.raise();
}

function groupByDay(data: MeasurementShiftDelay[], dayKeys: string[]) {
    const groups = new Map<string, Map<MeasurementShiftDelay['shiftSlot'], MeasurementShiftDelay>>();
    for (const dayKey of dayKeys) {
        groups.set(dayKey, new Map());
    }
    for (const shift of data) {
        const dayKey = getDayKey(shift.shiftEnd);
        const shifts = groups.get(dayKey);
        if (!shifts) continue;
        shifts.set(shift.shiftSlot, shift);
    }
    return Array.from(groups, ([dayKey, shifts]) => ({ dayKey, shifts }));
}

function getDayKey(value: Date): string {
    return DateTime.fromJSDate(new Date(value)).setZone('Europe/Helsinki').toISODate()!;
}

function formatDayLabel(dayKey: string): string[] {
    const date = DateTime.fromISO(dayKey, { zone: 'Europe/Helsinki' });
    return [date.toFormat('dd/MM'), date.toFormat('ccc')];
}

function getShiftSlotLabel(shiftSlot: typeof SHIFT_ORDER[number]): string {
    return shiftSlot === 'night' ? 'N' : shiftSlot === 'morning' ? 'M' : 'E';
}

function getTeamLabel(shift: MeasurementShiftDelay): string {
    return shift.shiftCode === 'A' || shift.shiftCode === 'B' ? shift.shiftCode : '';
}

function formatBarHours(minutes: number): string {
    const hours = minutes / 60;
    const roundedHours = Math.round(hours * 100) / 100;
    return `${roundedHours.toLocaleString('en-GB', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    })}h`;
}
